import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  Popper,
  Paper,
  List,
  ListItem,
  Checkbox,
  ListItemText,
  Button,
  Chip,
  IconButton,
} from "@mui/material";
import { ChevronDown } from "lucide-react";

const MultiSelectCheckEditCell = (params) => {
  const { id, field, value, colDef, api } = params;
  const initial = useMemo(() => (Array.isArray(value) ? value : []), [value]);
  const [selected, setSelected] = useState(initial);
  const [open, setOpen] = useState(false); // will open after anchor is ready
  const anchorRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [popperWidth, setPopperWidth] = useState(280);

  const options = useMemo(() => {
    // Prefer MUI valueOptions; fallback to AG-style cellEditorParams.options
    const vo = colDef?.valueOptions;
    if (Array.isArray(vo)) return vo;
    const opts = colDef?.cellEditorParams?.options;
    return Array.isArray(opts) ? opts : [];
  }, [colDef]);

  const toggle = (opt) => {
    setSelected((prev) =>
      prev.includes(opt) ? prev.filter((v) => v !== opt) : [...prev, opt]
    );
  };

  const handleDeleteChip = (chip) => {
    setSelected((prev) => prev.filter((v) => v !== chip));
  };

  const commit = () => {
    try {
      api.setEditCellValue({ id, field, value: selected });
      api.stopCellEditMode({ id, field });
    } catch (_) {}
  };

  const cancel = () => {
    try {
      api.stopCellEditMode({ id, field, ignoreModifications: true });
    } catch (_) {}
  };

  useEffect(() => {
    // focus input and set proper anchor once mounted
    try {
      const root =
        anchorRef.current?.querySelector(".MuiInputBase-root") ||
        anchorRef.current;
      if (root) {
        setAnchorEl(root);
        const rect = root.getBoundingClientRect();
        if (rect && rect.width) setPopperWidth(rect.width);
        const input = root.querySelector("input");
        input && input.focus();
        // open after anchor is established
        setOpen(true);
      }
    } catch (_) {}
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      commit();
    } else if (e.key === "Escape") {
      e.preventDefault();
      cancel();
    } else {
      // prevent grid from stealing navigation keys
      e.stopPropagation();
    }
  };

  return (
    <Box ref={anchorRef} sx={{ width: "100%", height: "100%" }}>
      <TextField
        size="small"
        variant="outlined"
        fullWidth
        onKeyDown={handleKeyDown}
        slotProps={{
          startAdornment: (
            <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", py: 0.25 }}>
              {selected.map((s) => (
                <Chip
                  key={s}
                  label={s}
                  size="small"
                  onDelete={() => handleDeleteChip(s)}
                  sx={{ height: 20, fontSize: 12 }}
                />
              ))}
            </Box>
          ),
          endAdornment: (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setOpen((o) => !o);
              }}
              sx={{ p: 0.5 }}
              aria-label="toggle dropdown"
            >
              <ChevronDown size={16} />
            </IconButton>
          ),
        }}
      />

      <Popper
        open={open && Boolean(anchorEl)}
        anchorEl={anchorEl}
        placement="bottom-start"
        modifiers={[
          // Slight negative offset to align Popper flush with the cell bottom
          { name: "offset", options: { offset: [0, -2] } },
          { name: "flip", enabled: true },
          { name: "preventOverflow", options: { boundary: "viewport" } },
        ]}
        style={{ zIndex: 20000, width: popperWidth || 280 }}
      >
        <Paper elevation={3} sx={{ p: 1, maxHeight: 280, overflowY: "auto" }}>
          <List dense disablePadding>
            {options.map((opt) => {
              const checked = selected.includes(opt);
              return (
                <ListItem
                  key={opt}
                  dense
                  button
                  onClick={() => toggle(opt)}
                  sx={{ py: 0.25 }}
                >
                  <Checkbox
                    size="small"
                    edge="start"
                    checked={checked}
                    tabIndex={-1}
                  />
                  <ListItemText
                    primary={opt}
                    slotProps={{
                      primary: {
                        typography: { fontSize: 13, fontWeight: 500 },
                      },
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
          <Box
            sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}
          >
            <Button size="small" variant="outlined" onClick={cancel}>
              Cancel
            </Button>
            <Button size="small" variant="contained" onClick={commit}>
              OK
            </Button>
          </Box>
        </Paper>
      </Popper>
    </Box>
  );
};

export default MultiSelectCheckEditCell;
